import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosGet, axiosPost } from '../api/axios';
import { toast } from 'react-toastify';

export type BackgroundJobStatus = 'pending' | 'running' | 'succeeded' | 'failed' | 'dead' | 'cancelled';

export interface BackgroundJob {
  id: number;
  status: BackgroundJobStatus;
  priority: number;
  queued_at: string;
  fire_at: string;
  fired_at: string | null;
  claimed_at: string | null;
  claimed_by: string | null;
  started_at: string | null;
  completed_at: string | null;
  attempts: number;
  max_attempts: number;
  last_error: string | null;
  last_traceback: string | null;
  executor: 'backend' | 'lambda' | null;
  queued_to_sqs_at: string | null;
  module: string | null;
  function: string | null;
  args: unknown[];
  kwargs: Record<string, unknown>;
  duration_seconds: number | null;
}

export interface BackgroundJobsResponse {
  count: number;
  data: BackgroundJob[];
}

export interface BackgroundJobStats {
  by_status: Record<string, number>;
  by_executor: Record<string, number>;
  pending_count: number;
  running_count: number;
  failed_count: number;
  dead_count: number;
  oldest_pending_age_seconds: number | null;
  sqs_in_flight_count: number;
  start_date?: string;
  end_date?: string;
}

interface BackgroundJobsParams {
  page?: number;
  limit?: number;
  status?: string;
  executor?: string;
  q?: string;
}

export interface BackgroundJobStatsParams {
  start_date: string;
  end_date: string;
}

const getBackgroundJobs = async (params: BackgroundJobsParams): Promise<BackgroundJobsResponse> => {
  const response = await axiosGet(
    '/background_jobs',
    {
      page: params.page || 1,
      limit: params.limit || 50,
      status: params.status || undefined,
      executor: params.executor || undefined,
      q: params.q || undefined,
    },
    'v3',
  );
  return response.data;
};

export const useBackgroundJobs = (params: BackgroundJobsParams) => {
  return useQuery<BackgroundJobsResponse>(['background_jobs', params], () => getBackgroundJobs(params));
};

const getBackgroundJobStats = async (params: BackgroundJobStatsParams): Promise<BackgroundJobStats> => {
  const response = await axiosGet(
    '/background_jobs/stats',
    {
      start_date: params.start_date,
      end_date: params.end_date,
    },
    'v3',
  );
  return response.data;
};

export const useBackgroundJobStats = (params: BackgroundJobStatsParams) => {
  return useQuery<BackgroundJobStats>(['background_job_stats', params], () => getBackgroundJobStats(params));
};

const retryBackgroundJob = async (jobId: number): Promise<BackgroundJob> => {
  const response = await axiosPost(`/background_jobs/${jobId}/retry`, {}, 'v3');
  return response.data;
};

export const useRetryBackgroundJob = () => {
  const queryClient = useQueryClient();
  return useMutation((jobId: number) => retryBackgroundJob(jobId), {
    onSuccess: () => {
      toast.success('Job requeued');
      queryClient.invalidateQueries(['background_jobs']);
      queryClient.invalidateQueries(['background_job_stats']);
    },
    onError: () => {
      toast.error('Failed to retry job');
    },
  });
};

const cancelBackgroundJob = async (jobId: number): Promise<BackgroundJob> => {
  const response = await axiosPost(`/background_jobs/${jobId}/cancel`, {}, 'v3');
  return response.data;
};

export const useCancelBackgroundJob = () => {
  const queryClient = useQueryClient();
  return useMutation((jobId: number) => cancelBackgroundJob(jobId), {
    onSuccess: () => {
      toast.success('Job cancelled');
      queryClient.invalidateQueries(['background_jobs']);
      queryClient.invalidateQueries(['background_job_stats']);
    },
    onError: () => {
      toast.error('Failed to cancel job');
    },
  });
};

export interface AbandonStaleResult {
  abandoned: number[];
  count: number;
}

const abandonStaleBackgroundJobs = async (payload?: {
  stale_after_minutes?: number;
  job_ids?: number[];
}): Promise<AbandonStaleResult> => {
  const response = await axiosPost('/background_jobs/abandon_stale', payload || {}, 'v3');
  return response.data;
};

export const useAbandonStaleBackgroundJobs = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload?: { stale_after_minutes?: number; job_ids?: number[] }) => abandonStaleBackgroundJobs(payload),
    {
      onSuccess: (result) => {
        toast.success(
          result.count > 0
            ? `Abandoned ${result.count} stuck running job${result.count === 1 ? '' : 's'}`
            : 'No stuck running jobs to abandon',
        );
        queryClient.invalidateQueries(['background_jobs']);
        queryClient.invalidateQueries(['background_job_stats']);
      },
      onError: () => {
        toast.error('Failed to abandon stuck jobs');
      },
    },
  );
};
